import type { CatalogFilter, CatalogSort } from './catalogTypes';
import type { CatalogItemTypeFilter } from './productTypes';

export const statusFilters: Array<{ id: CatalogFilter; label: string }> = [
  { id: 'active', label: 'En venta' },
  { id: 'inactive', label: 'Ocultos' },
];

/**
 * Valores por defecto de los filtros de catálogo.
 * Única fuente de verdad: la consumen el VM (init + clearFilters),
 * la pantalla (activeFilterCount, clearFilterDraft) y el sheet (hasActiveDraft).
 *
 * Nota: statusFilter default es 'active' (no 'all') porque el catálogo admin
 * muestra por defecto solo productos en venta. 'all' ("Todos") es una opción
 * válida del sheet de filtros y el server la soporta (appendStatusFilter no
 * agrega cláusula WHERE para 'all').
 */
export const DEFAULT_CATALOG_FILTERS = {
  itemTypeFilter: 'all' as CatalogItemTypeFilter,
  statusFilter: 'active' as CatalogFilter,
  categoryFilter: 'all' as string | null | 'all',
  // A→Z como default: orden predecible y estable para escanear el catálogo
  // (antes 'updated_desc': la lista "se movía" con cada edición).
  sortBy: 'name_asc' as CatalogSort,
  brandFilter: null as string | null,
  supplierFilter: null as string | null,
  alertFilter: false,
  incompleteFilter: false,
};

/**
 * Subconjunto de filtros comparables contra DEFAULT_CATALOG_FILTERS.
 * Acepta tanto el estado del VM como el draft del sheet (mismos campos).
 */
type CatalogFilterValues = {
  itemTypeFilter: CatalogItemTypeFilter;
  statusFilter: CatalogFilter;
  categoryFilter: string | null | 'all';
  sortBy: CatalogSort;
  brandFilter: string | null;
  supplierFilter: string | null;
  alertFilter: boolean;
  incompleteFilter: boolean;
};

/** True si algún filtro difiere de su default. */
export function hasActiveCatalogFilters(values: CatalogFilterValues): boolean {
  return (
    values.itemTypeFilter !== DEFAULT_CATALOG_FILTERS.itemTypeFilter ||
    values.statusFilter !== DEFAULT_CATALOG_FILTERS.statusFilter ||
    values.categoryFilter !== DEFAULT_CATALOG_FILTERS.categoryFilter ||
    values.sortBy !== DEFAULT_CATALOG_FILTERS.sortBy ||
    values.brandFilter !== DEFAULT_CATALOG_FILTERS.brandFilter ||
    values.supplierFilter !== DEFAULT_CATALOG_FILTERS.supplierFilter ||
    values.alertFilter !== DEFAULT_CATALOG_FILTERS.alertFilter ||
    values.incompleteFilter !== DEFAULT_CATALOG_FILTERS.incompleteFilter
  );
}

/** Cantidad de filtros que difieren de su default. */
export function countActiveCatalogFilters(values: CatalogFilterValues): number {
  let count = 0;
  if (values.itemTypeFilter !== DEFAULT_CATALOG_FILTERS.itemTypeFilter) count++;
  if (values.statusFilter !== DEFAULT_CATALOG_FILTERS.statusFilter) count++;
  if (values.categoryFilter !== DEFAULT_CATALOG_FILTERS.categoryFilter) count++;
  if (values.sortBy !== DEFAULT_CATALOG_FILTERS.sortBy) count++;
  if (values.brandFilter !== DEFAULT_CATALOG_FILTERS.brandFilter) count++;
  if (values.supplierFilter !== DEFAULT_CATALOG_FILTERS.supplierFilter) count++;
  if (values.alertFilter !== DEFAULT_CATALOG_FILTERS.alertFilter) count++;
  if (values.incompleteFilter !== DEFAULT_CATALOG_FILTERS.incompleteFilter) count++;
  return count;
}

/** Label corto para chips, strips y accesibilidad (filtro Tipo). */
export function catalogItemTypeFilterStripTitle(
  filter: CatalogItemTypeFilter,
): string | null {
  switch (filter) {
    case 'physical':
      return 'Solo productos';
    case 'service':
      return 'Solo servicios';
    case 'combo':
      return 'Solo combos';
    case 'recipe':
      return 'Con receta';
    default:
      return null;
  }
}

export const sortOptions: Array<{ id: CatalogSort; label: string }> = [
  { id: 'updated_desc', label: 'Últimos cambios' },
  { id: 'name_asc', label: 'Nombre A→Z' },
  { id: 'name_desc', label: 'Nombre Z→A' },
  { id: 'price_desc', label: 'Más caros' },
  { id: 'price_asc', label: 'Más baratos' },
  { id: 'stock_desc', label: 'Mayor stock' },
  { id: 'stock_asc', label: 'Menor stock' },
  { id: 'cost_desc', label: 'Mayor costo' },
  { id: 'cost_asc', label: 'Menor costo' },
  { id: 'margin_desc', label: 'Mayor margen' },
  { id: 'margin_asc', label: 'Menor margen' },
];
